import apiInstance from '@/api';
import toastMessage from '@/components/Toast';
import { ThunkApiConfig } from '@/models/ThunkApiConfig';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import downloadS3File from '@/utils/downloadS3File';
import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  ReportDownloadSuccessPayload,
  RevalueTradesMetricsRequestPayload,
} from '../types';

import {
  GetTaskProgressRequestPayload,
  getCVATaskProgress,
  getValueTaskProgress,
  revalueCVA,
  revalueTrades,
} from './actions';

export const revalueTradesBCVA = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'tradeCenter/revalueTradesBCVA',
  async (data: RevalueTradesMetricsRequestPayload, { dispatch }) => {
    dispatch(revalueCVA.request(data));

    try {
      const response = await apiInstance.revalueTrades.revalueTradesBCVA(data);
      dispatch(revalueCVA.success({ data: response.data.data }));
    } catch (error: any) {
      dispatch(revalueCVA.failure());
      const message = getThunkError(error);
      toastMessage.error(`Failed to revalue trades: ${message}`);
    }
  },
);

export const revalueTradesMetrics =
  createAsyncThunk.withTypes<ThunkApiConfig>()(
    'tradeCenter/revalueTradesMetrics',
    async (data: RevalueTradesMetricsRequestPayload, { dispatch }) => {
      dispatch(revalueTrades.request(data));

      try {
        const response =
          await apiInstance.revalueTrades.revalueTransactionValueMetrics(data);
        dispatch(revalueTrades.success({ data: response.data.data }));
      } catch (error: any) {
        dispatch(revalueTrades.failure());
        const message = getThunkError(error);
        toastMessage.error(`Failed to revalue trades: ${message}`);
      }
    },
  );

export const valueTaskProgresss = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'tradeCenter/getValueTaskProgress',
  async ({ task_id }: GetTaskProgressRequestPayload, { dispatch }) => {
    dispatch(getValueTaskProgress.request({ task_id }));

    try {
      const response = await apiInstance.revalueTrades.getTaskProgress(task_id);

      dispatch(getValueTaskProgress.success({ data: response.data.data }));
    } catch (error: any) {
      dispatch(getValueTaskProgress.failure({ task_id }));
      const message = getThunkError(error);
      toastMessage.error(`${task_id}: ${message}`);
    }
  },
);

export const cvaTaskProgresss = createAsyncThunk.withTypes<ThunkApiConfig>()(
  'tradeCenter/getCVATaskProgress',
  async ({ task_id }: GetTaskProgressRequestPayload, { dispatch }) => {
    dispatch(getCVATaskProgress.request({ task_id }));

    try {
      const response = await apiInstance.revalueTrades.getTaskProgress(task_id);

      dispatch(getCVATaskProgress.success({ data: response.data.data }));
    } catch (error: any) {
      dispatch(getCVATaskProgress.failure({ task_id }));
      const message = getThunkError(error);
      toastMessage.error(`${task_id}: ${message}`);
    }
  },
);

export const getReportDownload = createAsyncThunk<
  ReportDownloadSuccessPayload,
  string,
  { rejectValue: string }
>('tradeCenter/getReportDownload', async (payload, { rejectWithValue }) => {
  try {
    const response = await apiInstance.revalueTrades.getReportDownload(payload);
    const data = response.data.data;
    downloadS3File(data);
    toastMessage.success('Trades report downloaded successfully');
    return response.data.data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(`Failed to download report: ${message}`);
    return rejectWithValue(error.message || 'An error occurred.');
  }
});
